import classNames from "classnames";
import {
  ButtonHTMLAttributes,
  CSSProperties,
  ReactNode,
  forwardRef,
} from "react";
import { Spinner } from "reactstrap";

type Variant = "primary" | "secondary" | "outline" | "link";
type ColorScheme = "error" | "success" | "primary" | "warning" | "grayscale";
type IconPosition = "left" | "right";

interface CustomButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  colorScheme?: ColorScheme;
  icon?: ReactNode;
  iconPosition?: IconPosition;
  isLoading?: boolean;
  hasShadow?: boolean;
  fullWidth?: boolean;
  style?: CSSProperties;
  children?: ReactNode;
}

const CustomButtonV2 = forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      variant = "primary",
      colorScheme = "primary",
      icon,
      iconPosition = "left",
      isLoading = false,
      disabled = false,
      hasShadow = false,
      fullWidth = false,
      children,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled;
    const buttonClasses = classNames(
      "custom-button-v2",
      `custom-button-v2--${variant}`,
      `custom-button-v2--${variant}--${colorScheme}`,
      {
        "custom-button-v2--shadow": hasShadow,
        "custom-button-v2--loading": isLoading,
        "custom-button-v2--disabled": isDisabled,
        "custom-button-v2--fullwidth": fullWidth,
      },
      className
    );

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={isDisabled}
        style={style}
        {...props}
      >
        {isLoading && (
          <Spinner size="sm" className="custom-button-v2__spinner" />
        )}
        {!isLoading && icon && iconPosition === "left" && (
          <span className="custom-button-v2__icon custom-button-v2__icon--left">
            {icon}
          </span>
        )}
        <span className="custom-button-v2__content">{children}</span>
        {!isLoading && icon && iconPosition === "right" && (
          <span className="custom-button-v2__icon custom-button-v2__icon--right">
            {icon}
          </span>
        )}
      </button>
    );
  }
);

CustomButtonV2.displayName = "CustomButtonV2";

export default CustomButtonV2;
